import React, { Suspense, useEffect } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import {
	convertConfluenceLengthToCustomLength,
	convertConfluenceToneToCustomTone,
} from '../../../common/convertToCustomizationType';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { EmojiProviderContext } from '../../context/EmojiProviderContext';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useMouseOverWithDelayedExit } from '../../hooks/useMouseOverWithDelayedExit';

import type { AudioContainerQuery } from './__generated__/AudioContainerQuery.graphql';
import { AutoPlayManager } from './AutoPlayManager';
import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';

// Minimal interface for flags that works with both Atlaskit and Confluence flags
// Confluence's showFlag returns Promise<FlagDescriptor>, Atlaskit's returns DismissFn
export type FlagsContainer = {
	showFlag: (flag: Record<string, unknown>) => unknown;
};

const styles = cssMap({
	boxShadowWrapper: {
		boxShadow: token('elevation.shadow.overflow'),
		borderRadius: token('radius.xlarge'),
	},
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		width: '380px',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
	emojiProvider: EmojiProvider;
	flags: FlagsContainer;
};

export const AudioContainer = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
	emojiProvider,
	flags,
}: AudioContainerProps): React.JSX.Element => {
	const { activeFeature, activeContent, pendingContent, setCustomization } = useAudioPlayerStore();
	const { isMouseOver, handleMouseEnter, handleMouseLeave } = useMouseOverWithDelayedExit({
		delay: 3000,
		initiallyHovered: true,
		resetKey: pendingContent?.contentId,
	});

	const { updatePlayerState } = useAudioPlayerStore();

	useEffect(() => {
		updatePlayerState({
			product,
			cloudId,
			activationId,
		});
	}, [product, cloudId, activationId, updatePlayerState]);

	const { audioElementRef, fetchAudio } = useAudioPlayerStreaming({
		locale: 'en-US',
		type: activeFeature,
		format: AudioFormat.MP3,
		product,
		cloudId,
		activationId,
	});

	useAudioPlaybackSync({ audioRef: audioElementRef });

	const environment = useRelayEnvironment();

	useEffect(() => {
		const fetchAudioPreference = async () => {
			try {
				// TODO: Add playbackSpeed to the query once ready to be consumed
				const data = await fetchQuery<AudioContainerQuery>(
					environment,
					graphql`
						query AudioContainerQuery($cloudId: ID!) {
							confluence_getAudioPreference(cloudId: $cloudId) {
								tone
								length
							}
						}
					`,
					{ cloudId },
				).toPromise();

				if (data) {
					const graphqlTone = data?.confluence_getAudioPreference?.tone;
					const graphqlLength = data?.confluence_getAudioPreference?.length;

					const customTone = convertConfluenceToneToCustomTone(graphqlTone);
					const customLength = convertConfluenceLengthToCustomLength(graphqlLength);

					if (customTone && customLength) {
						setCustomization({
							tone: customTone,
							length: customLength,
						});
					}
				}
			} catch {
				// TODO: CCI-13829 Handle error appropriately
			}
		};

		void fetchAudioPreference();
	}, [environment, cloudId, setCustomization]);

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio}>
			{/* Enable auto-play of next track when current track ends */}
			<Suspense fallback={null}>
				<AutoPlayManager cloudId={cloudId} audioElementRef={audioElementRef} />
			</Suspense>
			<EmojiProviderContext.Provider value={{ emojiProvider }}>
				<Box xcss={styles.boxShadowWrapper}>
					<Box
						xcss={styles.container}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						{isMouseOver ? (
							<ExpandedAudioPlayer
								audioRef={audioElementRef}
								audioFeatures={audioFeatures}
								flags={flags}
							/>
						) : (
							<MinifiedAudioPlayer
								audioRef={audioElementRef}
								title={activeContent.title || 'Untitled'}
								byline={activeContent.author || ''}
								avatarUrl={activeContent.avatarUrl || ''}
							/>
						)}
						<audio ref={audioElementRef} />
					</Box>
				</Box>
			</EmojiProviderContext.Provider>
		</AudioPlayerStreamingProvider>
	);
};
